import React, { useState, useEffect } from 'react';
import { formatPhone } from '../../Utils/validations';
import './admin-dash.css';
import { Paper, Tabs, Tab, Box, Typography, CircularProgress, List, ListItem, ListItemButton, ListItemText, Button, ButtonGroup, Chip } from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import ViewListIcon from '@mui/icons-material/ViewList';
import PaymentIcon from '@mui/icons-material/Payment';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon from '@mui/icons-material/TableChart';
import AddCompany from '../AddCompany/add-company';
import { fetchCompanyView } from '../../Utils/api';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';

// Tab Panel Component
function TabPanel({ children, value, index }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function trialDaysRemaining(trialEndTs) {
    if (!trialEndTs) return null;
    const diff = Math.floor((new Date(trialEndTs) - new Date()) / (1000 * 60 * 60 * 24));
    return diff;
}

function AccountStatusChip({ company }) {
    const status = company.accountStatus || company.membership;
    if (status === 'TRIAL') {
        const days = trialDaysRemaining(company.trialEndTs);
        const expired = days !== null && days <= 0;
        return (
            <Chip
                label={expired ? 'Trial Expired' : `Trial · ${days}d left`}
                size="small"
                color={expired ? 'error' : days <= 7 ? 'warning' : 'info'}
                sx={{ ml: 1, fontSize: '0.7rem' }}
            />
        );
    }
    if (status === 'ACTIVE') return <Chip label="Active" size="small" color="success" sx={{ ml: 1, fontSize: '0.7rem' }} />;
    if (status === 'EXPIRED') return <Chip label="Expired" size="small" color="error" sx={{ ml: 1, fontSize: '0.7rem' }} />;
    return null;
}

const AdminDashboard = () => {
    const [activeTab, setActiveTab] = useState(0);
    const [activeSubTab, setActiveSubTab] = useState(0);
    const [companies, setCompanies] = useState([]);
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [loading, setLoading] = useState(false);

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    const handleSubTabChange = (event, newValue) => {
        setActiveSubTab(newValue);
    };

    // Fetch all companies when List of Companies or Payment Summary tab is active
    useEffect(() => {
        if (activeTab === 1 || activeTab === 2) {
            loadCompanies();
        }
    }, [activeTab]);

    const loadCompanies = async () => {
        setLoading(true);
        try {
            const companiesData = await fetchCompanyView();
            setCompanies(companiesData);
            // Auto-select first company if available
            if (companiesData.length > 0) {
                setSelectedCompany(companiesData[0]);
            }
        } catch (error) {
            console.error('Error loading companies:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCompanySelect = (company) => {
        setSelectedCompany(company);
    };

    // Export Companies to PDF
    const exportCompaniesToPDF = () => {
        const doc = new jsPDF();

        doc.setFontSize(18);
        doc.text('List of Companies', 14, 20);
        doc.setFontSize(11);
        doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 28);

        companies.forEach((company, index) => {
            if (index > 0) doc.addPage();

            doc.setFontSize(14);
            doc.text(company.companyName, 14, 40);

            // Company Information
            doc.setFontSize(12);
            doc.text('Company Information', 14, 50);
            const companyData = [
                ['Email', company.companyEmail || 'N/A'],
                ['Phone', company.companyPhone || 'N/A'],
                ['Address', company.companyAddress || 'N/A'],
                ['City', company.companyCity || 'N/A'],
                ['State', company.companyState || 'N/A'],
                ['Zip', company.companyZip || 'N/A']
            ];
            autoTable(doc, {
                startY: 55,
                head: [['Field', 'Value']],
                body: companyData,
                theme: 'grid',
                headStyles: { fillColor: [0, 34, 68] }
            });

            // Employees
            let finalY = doc.lastAutoTable.finalY + 10;
            doc.text(`Employees (${company.employees?.length || 0})`, 14, finalY);
            if (company.employees && company.employees.length > 0) {
                const employeeData = company.employees.map(emp => [
                    `${emp.firstName} ${emp.lastName}`,
                    emp.empDesignation || 'N/A',
                    emp.email || 'N/A',
                    emp.contactNumber || 'N/A'
                ]);
                autoTable(doc, {
                    startY: finalY + 5,
                    head: [['Name', 'Designation', 'Email', 'Phone']],
                    body: employeeData,
                    theme: 'striped',
                    headStyles: { fillColor: [0, 34, 68] }
                });
                finalY = doc.lastAutoTable.finalY + 10;
            } else {
                finalY += 10;
            }

            // Customers
            doc.text(`Customers (${company.customers?.length || 0})`, 14, finalY);
            if (company.customers && company.customers.length > 0) {
                const customerData = company.customers.map(cust => [
                    `${cust.custFirstName} ${cust.custLastName}`,
                    cust.custEmail || 'N/A',
                    formatPhone(cust.custContactNo) || 'N/A',
                    cust.custAddress || 'N/A'
                ]);
                autoTable(doc, {
                    startY: finalY + 5,
                    head: [['Name', 'Email', 'Phone', 'Address']],
                    body: customerData,
                    theme: 'striped',
                    headStyles: { fillColor: [0, 34, 68] }
                });
            }
        });

        doc.save('companies-list.pdf');
    };

    // Export Companies to Excel
    const exportCompaniesToExcel = () => {
        const workbook = XLSX.utils.book_new();

        companies.forEach((company) => {
            const worksheetData = [];

            // Company Info
            worksheetData.push(['Company Information']);
            worksheetData.push(['Company Name', company.companyName]);
            worksheetData.push(['Email', company.companyEmail || 'N/A']);
            worksheetData.push(['Phone', company.companyPhone || 'N/A']);
            worksheetData.push(['Address', company.companyAddress || 'N/A']);
            worksheetData.push(['City', company.companyCity || 'N/A']);
            worksheetData.push(['State', company.companyState || 'N/A']);
            worksheetData.push(['Zip', company.companyZip || 'N/A']);
            worksheetData.push([]);

            // Employees
            worksheetData.push([`Employees (${company.employees?.length || 0})`]);
            if (company.employees && company.employees.length > 0) {
                worksheetData.push(['Name', 'Designation', 'Email', 'Phone']);
                company.employees.forEach(emp => {
                    worksheetData.push([
                        `${emp.firstName} ${emp.lastName}`,
                        emp.empDesignation || 'N/A',
                        emp.email || 'N/A',
                        emp.contactNumber || 'N/A'
                    ]);
                });
            }
            worksheetData.push([]);

            // Customers
            worksheetData.push([`Customers (${company.customers?.length || 0})`]);
            if (company.customers && company.customers.length > 0) {
                worksheetData.push(['Name', 'Email', 'Phone', 'Address']);
                company.customers.forEach(cust => {
                    worksheetData.push([
                        `${cust.custFirstName} ${cust.custLastName}`,
                        cust.custEmail || 'N/A',
                        formatPhone(cust.custContactNo) || 'N/A',
                        cust.custAddress || 'N/A'
                    ]);
                });
            }

            const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
            XLSX.utils.book_append_sheet(workbook, worksheet, company.companyName.substring(0, 31));
        });

        XLSX.writeFile(workbook, 'companies-list.xlsx');
    };

    // Export Payment Summary to PDF
    const exportPaymentToPDF = () => {
        if (!selectedCompany) return;

        const doc = new jsPDF();

        doc.setFontSize(18);
        doc.text('Payment Summary', 14, 20);
        doc.setFontSize(12);
        doc.text(`Company: ${selectedCompany.companyName}`, 14, 30);
        doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 38);

        const months = ['January', 'February', 'March', 'April', 'May', 'June',
                       'July', 'August', 'September', 'October', 'November', 'December'];

        const paymentData = months.map(month => {
            const isPaid = Math.random() > 0.3;
            return [
                `${month} 2024`,
                isPaid ? 'PAID' : 'NOT PAID',
                isPaid ? '$50.00' : '-'
            ];
        });

        autoTable(doc, {
            startY: 45,
            head: [['Month', 'Status', 'Amount']],
            body: paymentData,
            theme: 'striped',
            headStyles: { fillColor: [0, 34, 68] },
            didParseCell: function(data) {
                if (data.section === 'body' && data.column.index === 1) {
                    if (data.cell.text[0] === 'PAID') {
                        data.cell.styles.textColor = [46, 125, 50];
                        data.cell.styles.fontStyle = 'bold';
                    } else {
                        data.cell.styles.textColor = [198, 40, 40];
                        data.cell.styles.fontStyle = 'bold';
                    }
                }
            }
        });

        doc.save(`payment-summary-${selectedCompany.companyName}.pdf`);
    };

    // Export Payment Summary to Excel
    const exportPaymentToExcel = () => {
        if (!selectedCompany) return;

        const months = ['January', 'February', 'March', 'April', 'May', 'June',
                       'July', 'August', 'September', 'October', 'November', 'December'];

        const worksheetData = [
            ['Payment Summary'],
            ['Company', selectedCompany.companyName],
            ['Generated on', new Date().toLocaleDateString()],
            [],
            ['Month', 'Status', 'Amount']
        ];

        months.forEach(month => {
            const isPaid = Math.random() > 0.3;
            worksheetData.push([
                `${month} 2024`,
                isPaid ? 'PAID' : 'NOT PAID',
                isPaid ? '$50.00' : '-'
            ]);
        });

        const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Payment Summary');
        XLSX.writeFile(workbook, `payment-summary-${selectedCompany.companyName}.xlsx`);
    };

    return (
        <div className="admin-dashboard">
            <h1 className='admin-dashboard-title'>Admin Dashboard</h1>

            <Paper elevation={3} sx={{ width: '95%', maxWidth: '1400px', marginTop: '20px' }}>
                <Box sx={{ borderBottom: 2, borderColor: '#e0e0e0', backgroundColor: '#f9f9f9' }}>
                    <Tabs
                        value={activeTab}
                        onChange={handleTabChange}
                        aria-label="admin dashboard tabs"
                        centered
                        sx={{
                            '& .MuiTab-root': {
                                fontSize: '1.1rem',
                                fontWeight: 600,
                                textTransform: 'none',
                                color: '#666',
                                padding: '16px 24px',
                                minHeight: '70px',
                            },
                            '& .Mui-selected': {
                                color: '#002244 !important',
                                fontWeight: 700,
                                backgroundColor: '#ffffff',
                            },
                            '& .MuiTabs-indicator': {
                                backgroundColor: '#002244',
                                height: 4,
                            },
                        }}
                    >
                        <Tab
                            icon={<BusinessIcon sx={{ fontSize: '1.8rem' }} />}
                            iconPosition="start"
                            label="Manage Companies"
                        />
                        <Tab
                            icon={<ViewListIcon sx={{ fontSize: '1.8rem' }} />}
                            iconPosition="start"
                            label="List of Companies"
                        />
                        <Tab
                            icon={<PaymentIcon sx={{ fontSize: '1.8rem' }} />}
                            iconPosition="start"
                            label="Payment Summary"
                        />
                    </Tabs>
                </Box>

                <TabPanel value={activeTab} index={0}>
                    {/* Sub-tabs for Manage Companies */}
                    <Box sx={{ borderBottom: 1, borderColor: 'divider', backgroundColor: '#fafafa' }}>
                        <Tabs
                            value={activeSubTab}
                            onChange={handleSubTabChange}
                            aria-label="manage companies sub-tabs"
                            sx={{
                                '& .MuiTab-root': {
                                    fontSize: '0.95rem',
                                    fontWeight: 500,
                                    textTransform: 'none',
                                    color: '#666',
                                    minHeight: '50px',
                                },
                                '& .Mui-selected': {
                                    color: '#002244 !important',
                                    fontWeight: 600,
                                },
                                '& .MuiTabs-indicator': {
                                    backgroundColor: '#002244',
                                    height: 3,
                                },
                            }}
                        >
                            <Tab
                                icon={<AddIcon />}
                                iconPosition="start"
                                label="Create Company"
                            />
                            <Tab
                                icon={<EditIcon />}
                                iconPosition="start"
                                label="Edit Company"
                            />
                            <Tab
                                icon={<DeleteIcon />}
                                iconPosition="start"
                                label="Deactivate Company"
                            />
                        </Tabs>
                    </Box>

                    {/* Sub-tab panels */}
                    <TabPanel value={activeSubTab} index={0}>
                        <Box sx={{ padding: 0 }}>
                            <AddCompany />
                        </Box>
                    </TabPanel>

                    <TabPanel value={activeSubTab} index={1}>
                        <div className="tab-content">
                            <h2>Edit Company Details</h2>
                            <p>Select and edit company information</p>
                            {/* Edit company content will be added here */}
                        </div>
                    </TabPanel>

                    <TabPanel value={activeSubTab} index={2}>
                        <div className="tab-content">
                            <h2>Deactivate Company</h2>
                            <p>Select a company to deactivate from the system</p>
                            {/* Deactivate company content will be added here */}
                        </div>
                    </TabPanel>
                </TabPanel>

                <TabPanel value={activeTab} index={1}>
                    {/* Export Buttons */}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '15px', gap: 2 }}>
                        <ButtonGroup variant="outlined" size="small">
                            <Button
                                startIcon={<PictureAsPdfIcon />}
                                onClick={exportCompaniesToPDF}
                                disabled={companies.length === 0}
                                sx={{ color: '#002244', borderColor: '#002244' }}
                            >
                                Export to PDF
                            </Button>
                            <Button
                                startIcon={<TableChartIcon />}
                                onClick={exportCompaniesToExcel}
                                disabled={companies.length === 0}
                                sx={{ color: '#002244', borderColor: '#002244' }}
                            >
                                Export to Excel
                            </Button>
                        </ButtonGroup>
                    </Box>
                    <Box sx={{ display: 'flex', height: '600px', gap: 2 }}>
                        {/* Left Side - Company List */}
                        <Paper
                            elevation={2}
                            sx={{
                                width: '300px',
                                overflow: 'auto',
                                borderRight: '2px solid #e0e0e0'
                            }}
                        >
                            <Box sx={{
                                padding: '16px',
                                backgroundColor: '#f5f5f5',
                                borderBottom: '1px solid #e0e0e0',
                                position: 'sticky',
                                top: 0,
                                zIndex: 1
                            }}>
                                <Typography variant="h6" sx={{ color: '#002244', fontWeight: 600 }}>
                                    Companies
                                </Typography>
                            </Box>

                            {loading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
                                    <CircularProgress />
                                </Box>
                            ) : companies.length === 0 ? (
                                <Typography sx={{ textAlign: 'center', color: '#666', padding: '20px' }}>
                                    No companies found
                                </Typography>
                            ) : (
                                <List sx={{ padding: 0 }}>
                                    {companies.map((company) => (
                                        <ListItem key={company.companyId} disablePadding>
                                            <ListItemButton
                                                selected={selectedCompany?.companyId === company.companyId}
                                                onClick={() => handleCompanySelect(company)}
                                                sx={{
                                                    '&.Mui-selected': {
                                                        backgroundColor: '#e3f2fd',
                                                        borderLeft: '4px solid #002244',
                                                        '&:hover': { backgroundColor: '#bbdefb' }
                                                    },
                                                    '&:hover': { backgroundColor: '#f5f5f5' }
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                                    <Typography
                                                        sx={{
                                                            fontWeight: selectedCompany?.companyId === company.companyId ? 600 : 400,
                                                            color: '#002244',
                                                            fontSize: '0.9rem'
                                                        }}
                                                    >
                                                        {company.companyName}
                                                    </Typography>
                                                    <AccountStatusChip company={company} />
                                                </Box>
                                            </ListItemButton>
                                        </ListItem>
                                    ))}
                                </List>
                            )}
                        </Paper>

                        {/* Right Side - Company Details */}
                        <Box sx={{ flex: 1, overflow: 'auto', padding: '20px' }}>
                            {selectedCompany ? (
                                <Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, marginBottom: '20px' }}>
                                        <Typography variant="h5" sx={{ color: '#002244', fontWeight: 600 }}>
                                            {selectedCompany.companyName}
                                        </Typography>
                                        <AccountStatusChip company={selectedCompany} />
                                    </Box>

                                    {/* Trial & Subscription Section */}
                                    {(selectedCompany.accountStatus === 'TRIAL' || selectedCompany.membership === 'TRIAL') && (
                                        <Box sx={{ marginBottom: '30px', padding: '20px', backgroundColor: '#fff8e1', border: '1px solid #ffe082', borderRadius: '8px' }}>
                                            <Typography variant="h6" sx={{ color: '#e65100', fontWeight: 600, marginBottom: '15px' }}>
                                                Trial & Subscription
                                            </Typography>
                                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
                                                <Typography><strong>Status:</strong> Free Trial</Typography>
                                                <Typography>
                                                    <strong>Days Remaining:</strong>{' '}
                                                    {trialDaysRemaining(selectedCompany.trialEndTs) !== null
                                                        ? Math.max(0, trialDaysRemaining(selectedCompany.trialEndTs)) + ' days'
                                                        : 'N/A'}
                                                </Typography>
                                                <Typography>
                                                    <strong>Trial Start:</strong>{' '}
                                                    {selectedCompany.trialStartTs
                                                        ? new Date(selectedCompany.trialStartTs).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
                                                        : 'N/A'}
                                                </Typography>
                                                <Typography>
                                                    <strong>Trial End:</strong>{' '}
                                                    {selectedCompany.trialEndTs
                                                        ? new Date(selectedCompany.trialEndTs).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
                                                        : 'N/A'}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    )}

                                    {/* Company Info Section */}
                                    <Box sx={{ marginBottom: '30px', padding: '20px', backgroundColor: '#f9f9f9', borderRadius: '8px' }}>
                                        <Typography variant="h6" sx={{ color: '#002244', fontWeight: 600, marginBottom: '15px' }}>
                                            Company Information
                                        </Typography>
                                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
                                            <Typography><strong>Email:</strong> {selectedCompany.companyEmail || 'N/A'}</Typography>
                                            <Typography><strong>Phone:</strong> {selectedCompany.companyPhone || 'N/A'}</Typography>
                                            <Typography><strong>Address:</strong> {selectedCompany.companyAddress || 'N/A'}</Typography>
                                            <Typography><strong>City:</strong> {selectedCompany.companyCity || 'N/A'}</Typography>
                                            <Typography><strong>State:</strong> {selectedCompany.companyState || 'N/A'}</Typography>
                                            <Typography><strong>Zip:</strong> {selectedCompany.companyZip || 'N/A'}</Typography>
                                        </Box>
                                    </Box>

                                    {/* Employees Section */}
                                    <Box sx={{ marginBottom: '30px' }}>
                                        <Typography variant="h6" sx={{ color: '#002244', fontWeight: 600, marginBottom: '15px' }}>
                                            Employees ({selectedCompany.employees?.length || 0})
                                        </Typography>
                                        {selectedCompany.employees && selectedCompany.employees.length > 0 ? (
                                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '15px' }}>
                                                {selectedCompany.employees.map((employee) => (
                                                    <Box
                                                        key={employee.employeeId}
                                                        sx={{
                                                            padding: '15px',
                                                            border: '1px solid #e0e0e0',
                                                            borderRadius: '8px',
                                                            backgroundColor: '#fff',
                                                            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                                                        }}
                                                    >
                                                        <Typography sx={{ fontWeight: 600, color: '#002244', marginBottom: '8px' }}>
                                                            {employee.firstName} {employee.lastName}
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ color: '#666', marginBottom: '4px' }}>
                                                            <strong>Designation:</strong> {employee.empDesignation || 'N/A'}
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ color: '#666', marginBottom: '4px' }}>
                                                            <strong>Email:</strong> {employee.email || 'N/A'}
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ color: '#666' }}>
                                                            <strong>Phone:</strong> {employee.contactNumber || 'N/A'}
                                                        </Typography>
                                                    </Box>
                                                ))}
                                            </Box>
                                        ) : (
                                            <Typography sx={{ color: '#999', fontStyle: 'italic' }}>No employees found</Typography>
                                        )}
                                    </Box>

                                    {/* Customers Section */}
                                    <Box>
                                        <Typography variant="h6" sx={{ color: '#002244', fontWeight: 600, marginBottom: '15px' }}>
                                            Customers ({selectedCompany.customers?.length || 0})
                                        </Typography>
                                        {selectedCompany.customers && selectedCompany.customers.length > 0 ? (
                                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '15px' }}>
                                                {selectedCompany.customers.map((customer) => (
                                                    <Box
                                                        key={customer.customerId}
                                                        sx={{
                                                            padding: '15px',
                                                            border: '1px solid #e0e0e0',
                                                            borderRadius: '8px',
                                                            backgroundColor: '#fff',
                                                            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                                                        }}
                                                    >
                                                        <Typography sx={{ fontWeight: 600, color: '#002244', marginBottom: '8px' }}>
                                                            {customer.custFirstName} {customer.custLastName}
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ color: '#666', marginBottom: '4px' }}>
                                                            <strong>Email:</strong> {customer.custEmail || 'N/A'}
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ color: '#666', marginBottom: '4px' }}>
                                                            <strong>Phone:</strong> {formatPhone(customer.custContactNo) || 'N/A'}
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ color: '#666' }}>
                                                            <strong>Address:</strong> {customer.custAddress || 'N/A'}
                                                        </Typography>
                                                    </Box>
                                                ))}
                                            </Box>
                                        ) : (
                                            <Typography sx={{ color: '#999', fontStyle: 'italic' }}>No customers found</Typography>
                                        )}
                                    </Box>
                                </Box>
                            ) : (
                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                    <Typography sx={{ color: '#999', fontSize: '1.2rem' }}>
                                        Select a company to view details
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Box>
                </TabPanel>

                <TabPanel value={activeTab} index={2}>
                    {/* Export Buttons */}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '15px', gap: 2 }}>
                        <ButtonGroup variant="outlined" size="small">
                            <Button
                                startIcon={<PictureAsPdfIcon />}
                                onClick={exportPaymentToPDF}
                                disabled={!selectedCompany}
                                sx={{ color: '#002244', borderColor: '#002244' }}
                            >
                                Export to PDF
                            </Button>
                            <Button
                                startIcon={<TableChartIcon />}
                                onClick={exportPaymentToExcel}
                                disabled={!selectedCompany}
                                sx={{ color: '#002244', borderColor: '#002244' }}
                            >
                                Export to Excel
                            </Button>
                        </ButtonGroup>
                    </Box>
                    <Box sx={{ display: 'flex', height: '600px', gap: 2 }}>
                        {/* Left Side - Company List */}
                        <Paper
                            elevation={2}
                            sx={{
                                width: '300px',
                                overflow: 'auto',
                                borderRight: '2px solid #e0e0e0'
                            }}
                        >
                            <Box sx={{
                                padding: '16px',
                                backgroundColor: '#f5f5f5',
                                borderBottom: '1px solid #e0e0e0',
                                position: 'sticky',
                                top: 0,
                                zIndex: 1
                            }}>
                                <Typography variant="h6" sx={{ color: '#002244', fontWeight: 600 }}>
                                    Companies
                                </Typography>
                            </Box>

                            {loading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
                                    <CircularProgress />
                                </Box>
                            ) : companies.length === 0 ? (
                                <Typography sx={{ textAlign: 'center', color: '#666', padding: '20px' }}>
                                    No companies found
                                </Typography>
                            ) : (
                                <List sx={{ padding: 0 }}>
                                    {companies.map((company) => (
                                        <ListItem key={company.companyId} disablePadding>
                                            <ListItemButton
                                                selected={selectedCompany?.companyId === company.companyId}
                                                onClick={() => handleCompanySelect(company)}
                                                sx={{
                                                    '&.Mui-selected': {
                                                        backgroundColor: '#e3f2fd',
                                                        borderLeft: '4px solid #002244',
                                                        '&:hover': { backgroundColor: '#bbdefb' }
                                                    },
                                                    '&:hover': { backgroundColor: '#f5f5f5' }
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                                    <Typography
                                                        sx={{
                                                            fontWeight: selectedCompany?.companyId === company.companyId ? 600 : 400,
                                                            color: '#002244',
                                                            fontSize: '0.9rem'
                                                        }}
                                                    >
                                                        {company.companyName}
                                                    </Typography>
                                                    <AccountStatusChip company={company} />
                                                </Box>
                                            </ListItemButton>
                                        </ListItem>
                                    ))}
                                </List>
                            )}
                        </Paper>

                        {/* Right Side - Payment Details */}
                        <Box sx={{ flex: 1, overflow: 'auto', padding: '20px' }}>
                            {selectedCompany ? (
                                <Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, marginBottom: '20px' }}>
                                        <Typography variant="h5" sx={{ color: '#002244', fontWeight: 600 }}>
                                            {selectedCompany.companyName} - Payment Summary
                                        </Typography>
                                        <AccountStatusChip company={selectedCompany} />
                                    </Box>

                                    {/* Trial Banner */}
                                    {(selectedCompany.accountStatus === 'TRIAL' || selectedCompany.membership === 'TRIAL') ? (
                                        <Box sx={{ padding: '20px', backgroundColor: '#fff8e1', border: '1px solid #ffe082', borderRadius: '8px' }}>
                                            <Typography variant="h6" sx={{ color: '#e65100', fontWeight: 600, marginBottom: '15px' }}>
                                                Free Trial Period
                                            </Typography>
                                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
                                                <Typography><strong>Status:</strong> Free Trial (no charges)</Typography>
                                                <Typography>
                                                    <strong>Days Remaining:</strong>{' '}
                                                    {trialDaysRemaining(selectedCompany.trialEndTs) !== null
                                                        ? Math.max(0, trialDaysRemaining(selectedCompany.trialEndTs)) + ' days'
                                                        : 'N/A'}
                                                </Typography>
                                                <Typography>
                                                    <strong>Trial Start:</strong>{' '}
                                                    {selectedCompany.trialStartTs
                                                        ? new Date(selectedCompany.trialStartTs).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
                                                        : 'N/A'}
                                                </Typography>
                                                <Typography>
                                                    <strong>Trial End:</strong>{' '}
                                                    {selectedCompany.trialEndTs
                                                        ? new Date(selectedCompany.trialEndTs).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
                                                        : 'N/A'}
                                                </Typography>
                                            </Box>
                                            <Typography sx={{ marginTop: '15px', color: '#666', fontSize: '0.9rem', fontStyle: 'italic' }}>
                                                No payment history yet. Billing begins after the free trial ends and the company subscribes to a paid plan.
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <Box>
                                            {/* Payment Status Table */}
                                            <Box sx={{ marginBottom: '30px' }}>
                                                <Typography variant="h6" sx={{ color: '#002244', fontWeight: 600, marginBottom: '15px' }}>
                                                    Monthly Payment Status
                                                </Typography>
                                                <Box sx={{
                                                    display: 'grid',
                                                    gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
                                                    gap: '15px'
                                                }}>
                                                    {['January', 'February', 'March', 'April', 'May', 'June',
                                                      'July', 'August', 'September', 'October', 'November', 'December'].map((month) => {
                                                        const isPaid = Math.random() > 0.3;
                                                        return (
                                                            <Box
                                                                key={month}
                                                                sx={{
                                                                    padding: '15px',
                                                                    border: '1px solid #e0e0e0',
                                                                    borderRadius: '8px',
                                                                    backgroundColor: isPaid ? '#e8f5e9' : '#ffebee',
                                                                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                                                                }}
                                                            >
                                                                <Typography sx={{ fontWeight: 600, color: '#002244', marginBottom: '8px' }}>
                                                                    {month} 2024
                                                                </Typography>
                                                                <Typography
                                                                    variant="body2"
                                                                    sx={{ color: isPaid ? '#2e7d32' : '#c62828', fontWeight: 600, fontSize: '0.95rem' }}
                                                                >
                                                                    Status: {isPaid ? 'PAID' : 'NOT PAID'}
                                                                </Typography>
                                                                {isPaid && (
                                                                    <Typography variant="body2" sx={{ color: '#666', marginTop: '4px' }}>
                                                                        Amount: $50.00
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                        );
                                                    })}
                                                </Box>
                                            </Box>

                                            {/* Payment Summary Info */}
                                            <Box sx={{ padding: '20px', backgroundColor: '#f9f9f9', borderRadius: '8px' }}>
                                                <Typography variant="h6" sx={{ color: '#002244', fontWeight: 600, marginBottom: '15px' }}>
                                                    Summary
                                                </Typography>
                                                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
                                                    <Typography><strong>Total Months:</strong> 12</Typography>
                                                    <Typography><strong>Paid Months:</strong> 8</Typography>
                                                    <Typography><strong>Pending Months:</strong> 4</Typography>
                                                    <Typography><strong>Total Amount Paid:</strong> $400.00</Typography>
                                                </Box>
                                                <Typography sx={{ marginTop: '15px', color: '#666', fontSize: '0.9rem', fontStyle: 'italic' }}>
                                                    Note: Payment integration with Manage Subscriptions will be implemented later
                                                </Typography>
                                            </Box>
                                        </Box>
                                    )}
                                </Box>
                            ) : (
                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                    <Typography sx={{ color: '#999', fontSize: '1.2rem' }}>
                                        Select a company to view payment summary
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Box>
                </TabPanel>
            </Paper>
        </div>
    );
};

export default AdminDashboard;